import { computed, ref } from 'vue'
import { useDropZone as useDropZoneBase, useFileDialog } from '@vueuse/core'

import type { ComputedRef, Ref } from 'vue'
import type { DropZoneAreaProps } from '../types/interfaces'

import { DROP_ZONE_ACCEPT } from '../types/enums'

interface UseDropZoneAreaOptions {
  props: DropZoneAreaProps
  onFiles: (files: File[]) => void
}

interface UseDropZoneAreaReturn {
  areaRef: Ref<HTMLElement | null>
  isOverDropZone: Ref<boolean>
  rootClasses: ComputedRef<(string | Record<string, boolean>)[]>
  triggerBrowse: () => void
}

export const useDropZoneArea = (
  options: UseDropZoneAreaOptions,
): UseDropZoneAreaReturn => {
  const { props, onFiles } = options

  const areaRef = ref<HTMLElement | null>(null)
  const isMultiple = props.multiple ?? true
  const acceptAttr = (props.accept ?? DROP_ZONE_ACCEPT).join(',')

  const { isOverDropZone } = useDropZoneBase(areaRef, {
    multiple: isMultiple,
    onDrop: (files) => {
      if (props.disabled) return
      if (files?.length) onFiles(files)
    },
  })

  const { open, onChange, reset } = useFileDialog({
    accept: acceptAttr,
    multiple: isMultiple,
  })

  onChange((fileList) => {
    if (!fileList || fileList.length === 0) return
    onFiles(Array.from(fileList))
    reset()
  })

  const triggerBrowse = (): void => {
    if (props.disabled) return
    open()
  }

  const rootClasses = computed<(string | Record<string, boolean>)[]>(() => [
    'drop-zone-area',
    {
      'drop-zone-area--over': isOverDropZone.value && !props.disabled,
      'drop-zone-area--disabled': Boolean(props.disabled),
    },
  ])

  return {
    areaRef,
    isOverDropZone,
    rootClasses,
    triggerBrowse,
  }
}
